import type { components } from "@countdown/api-client";
import { MAX_SLIDES } from "@countdown/door-editor";
import { type ReactElement, useState } from "react";

import { ConfirmButton } from "@/components/ConfirmButton";
import { EmbedSettings } from "@/components/EmbedSettings";
import { FormField } from "@/components/FormField";
import { HelpTip } from "@/components/HelpTip";
import { Notice } from "@/components/Notice";
import { SaveForm } from "@/components/SaveForm";
import { SETTING_GROUPS, StyleTransfer, copyableGroups } from "@/components/StyleTransfer";
import { Advanced, Section } from "@/components/Section";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type ActionDetail, moveAction, saveActionSection } from "@/lib/api";
import { CUSTOMER_HELP_TIPS, HELP_TIPS, SPONSOR_RIGHT_TIPS } from "@/lib/help";
import {
  SPONSOR_RIGHT_LABELS,
  type SponsorRight,
  can,
  formatDate,
  formatDateTime,
} from "@/lib/labels";
import { type Wording, wordingOf } from "@/lib/wording";
import { DetailState, useActionContext, useActionDetail } from "@/pages/ActionPage";
import { TIME_ZONES } from "@/pages/ActionsPage";
import { FormProposal } from "@/pages/FormPage";

/** A trimmed text field; empty when absent. */
function text(form: FormData, name: string): string {
  const raw = form.get(name);
  return typeof raw === "string" ? raw.trim() : "";
}

function textOrNull(form: FormData, name: string): string | null {
  const value = text(form, name);
  return value === "" ? null : value;
}

/** The addresses of a text field, separated by line breaks, spaces, commas or semicolons. */
export function addressesOf(raw: string): string[] {
  return raw.split(/[\s,;]+/).filter((entry) => entry !== "");
}

/** A number field; `null` when left empty. */
function numberOrNull(form: FormData, name: string): number | null {
  const value = text(form, name);
  return value === "" ? null : Number(value);
}

type SponsorRights = components["schemas"]["SponsorRights"];

const RIGHTS = Object.keys(SPONSOR_RIGHT_LABELS) as SponsorRight[];

/** The rights of the form „Pflege durch Sponsoren", whole: an unchecked box is an answer. The
 *  customer's areas of the portal (E6b) are not among them. */
export function sponsorRightsOf(form: FormData): Omit<SponsorRights, "embed"> {
  return {
    sponsor_edits: form.get("sponsor_edits") !== null,
    pictures: form.get("pictures") !== null,
    max_slides: Number(text(form, "max_slides")),
    slide_structure: form.get("slide_structure") !== null,
    quiz: form.get("quiz") !== null,
    videos: form.get("videos") !== null,
    link_buttons: form.get("link_buttons") !== null,
    prize: form.get("prize") !== null,
    self_publish: form.get("self_publish") !== null,
  };
}

/**
 * Whether sponsors design the doors of this action themselves and what they may change in the
 * configurator (H1, H5, E6), with the element of its own (D57): it decides who approves, and
 * the superadmin decides who sets it. A sponsor may deviate on its own page.
 *
 * The rights apply only while sponsors edit, „Bilder und Logo liefern" only while they do not.
 * Both blocks stay mounted and are hidden: `sponsorRightsOf` reads a missing box as a refusal,
 * so an unmounted block would refuse everything with the next save.
 */
function SponsorWorkflowForm({
  data,
  readOnly,
  onSaved,
}: {
  readonly data: ActionDetail;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
}): ReactElement {
  const rights = data.sponsor_rights;
  const [edits, setEdits] = useState(rights.sponsor_edits);
  return (
    <SaveForm
      label="Pflege durch Sponsoren"
      readOnly={readOnly}
      onSaved={onSaved}
      submit={(form) =>
        saveActionSection(
          "sponsor-workflow",
          { rights: { embed: rights.embed, ...sponsorRightsOf(form) } },
          data.action_id,
        )
      }
    >
      <div className="flex items-center gap-2">
        <input
          className="border border-input"
          id="sponsor_edits"
          name="sponsor_edits"
          type="checkbox"
          defaultChecked={rights.sponsor_edits}
          onChange={(event) => {
            setEdits(event.currentTarget.checked);
          }}
        />
        <label className="text-sm font-medium" htmlFor="sponsor_edits">
          Sponsor pflegt selbst
        </label>
        <HelpTip topic="Sponsor pflegt selbst">{HELP_TIPS.sponsorEdits}</HelpTip>
      </div>
      <div hidden={!edits}>
        <div className="mt-4 grid justify-items-start gap-1.5">
          <Label htmlFor="max_slides">Höchstzahl Folien</Label>
          <select
            className="h-9 border border-input bg-background px-2 text-sm"
            id="max_slides"
            name="max_slides"
            defaultValue={String(rights.max_slides)}
          >
            {Array.from({ length: MAX_SLIDES }, (_, index) => (
              <option key={index} value={index + 1}>
                {index + 1}
              </option>
            ))}
          </select>
        </div>
        <fieldset className="mt-4">
          <legend className="text-sm font-medium">Sponsoren dürfen</legend>
          {RIGHTS.map((right) => (
            <div className="mt-2 flex items-center gap-2" key={right}>
              <input
                className="border border-input"
                id={`right-${right}`}
                name={right}
                type="checkbox"
                defaultChecked={rights[right]}
              />
              <label className="text-sm" htmlFor={`right-${right}`}>
                {SPONSOR_RIGHT_LABELS[right]}
              </label>
              {SPONSOR_RIGHT_TIPS[right] === undefined ? null : (
                <HelpTip topic={SPONSOR_RIGHT_LABELS[right]}>{SPONSOR_RIGHT_TIPS[right]}</HelpTip>
              )}
            </div>
          ))}
        </fieldset>
        <div className="mt-4 flex items-center gap-2">
          <input
            className="border border-input"
            id="self_publish"
            name="self_publish"
            type="checkbox"
            defaultChecked={rights.self_publish}
          />
          <label className="text-sm" htmlFor="self_publish">
            Sponsor veröffentlicht ohne Freigabe
          </label>
          <HelpTip topic="Sponsor veröffentlicht ohne Freigabe">{HELP_TIPS.selfPublish}</HelpTip>
        </div>
      </div>
      <div hidden={edits}>
        <p className="mt-4 text-sm text-muted-foreground">
          Sponsoren sehen ihre Türchen an, geben sie frei oder wünschen Änderungen.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <input
            className="border border-input"
            id="pictures"
            name="pictures"
            type="checkbox"
            defaultChecked={rights.pictures}
          />
          <label className="text-sm" htmlFor="pictures">
            Bilder und Logo liefern
          </label>
          <HelpTip topic="Bilder und Logo liefern">{HELP_TIPS.sponsorPictures}</HelpTip>
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        Gilt für alle Sponsoren dieser Aktion, außer ein Sponsor hat eine eigene Einstellung.
      </p>
    </SaveForm>
  );
}

/**
 * What the customer of a customer's calendar does in its calendar access beyond its doors, its
 * leads and its report (E6b): one switch per area of the server's list that has one, off when
 * the calendar is created. Saved with the rest of the action's rights, which stay as they are.
 */
function CalendarAccessForm({
  data,
  readOnly,
  onSaved,
}: {
  readonly data: ActionDetail;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
}): ReactElement {
  const switches = data.portal_functions.flatMap((entry) =>
    entry.right === null ? [] : [{ right: entry.right, label: entry.label }],
  );
  return (
    <SaveForm
      label="Kalenderzugang"
      readOnly={readOnly}
      onSaved={onSaved}
      submit={(form) => {
        const rights = { ...data.sponsor_rights };
        for (const { right } of switches) {
          rights[right] = form.has(right);
        }
        return saveActionSection("sponsor-workflow", { rights }, data.action_id);
      }}
    >
      {switches.map(({ right, label }) => (
        <div className="mt-2 flex items-center gap-2" key={right}>
          <input
            className="border border-input"
            id={`access-${right}`}
            name={right}
            type="checkbox"
            defaultChecked={data.sponsor_rights[right]}
          />
          <label className="text-sm" htmlFor={`access-${right}`}>
            {label}
          </label>
        </div>
      ))}
    </SaveForm>
  );
}

/**
 * Link, schedule and the deadlines without a form of their own, the superadmin's alone
 * (02.11.2026). Its own button rather than the save bar: the server puts every door on its
 * day again, which should not ride along with a title change.
 */
function ScheduleForm({
  data,
  readOnly,
  onSaved,
}: {
  readonly data: ActionDetail;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
}): ReactElement {
  return (
    <SaveForm
      label="Zeitplan und Link"
      readOnly={readOnly}
      onSaved={onSaved}
      submitLabel="Zeitplan übernehmen"
      submit={(form) =>
        saveActionSection(
          "schedule",
          {
            stable_link_slug: text(form, "stable_link_slug"),
            starts_on: text(form, "starts_on"),
            ends_on: text(form, "ends_on"),
            field_count: Number(text(form, "field_count")),
            timezone: text(form, "timezone"),
            followup_period_days: numberOrNull(form, "followup_period_days"),
            grace_period_minutes: numberOrNull(form, "grace_period_minutes"),
            winner_response_days: numberOrNull(form, "winner_response_days"),
            substitute_draw_limit: Number(text(form, "substitute_draw_limit")),
          },
          data.action_id,
        )
      }
    >
      {data.status === "draft" || readOnly ? null : (
        <Notice tone="info" className="mb-4">
          Gilt sofort: Ein neuer Link macht gedruckte Links und QR-Codes ungültig, jedes Türchen
          geht an seinem neuen Tag auf.
        </Notice>
      )}
      <div className="grid gap-x-6 sm:grid-cols-2">
        <FormField
          label="Link"
          name="stable_link_slug"
          defaultValue={data.stable_link_slug}
          required
          maxLength={120}
          pattern="[a-z0-9]+(-[a-z0-9]+)*"
        />
        <FormField
          label="Zeitzone"
          name="timezone"
          defaultValue={data.timezone}
          required
          list="zeitzonen"
        />
        <FormField label="Beginn" name="starts_on" type="date" defaultValue={data.starts_on} required />
        <FormField label="Ende" name="ends_on" type="date" defaultValue={data.ends_on} required />
        <FormField
          label="Anzahl Türchen"
          name="field_count"
          type="number"
          defaultValue={data.field_count}
          required
          min={1}
          max={366}
        />
        <FormField
          label="Nachlauffrist (Tage)"
          name="followup_period_days"
          type="number"
          defaultValue={data.followup_period_days}
          min={0}
          max={365}
          placeholder="14"
        />
        <FormField
          label="Kulanzfenster (Minuten)"
          name="grace_period_minutes"
          type="number"
          defaultValue={data.grace_period_minutes}
          min={0}
          max={10080}
          placeholder={`Standard: ${String(data.grace_period_default_minutes)}`}
          help={`So lange nach dem Ende eines Türchens kann eine begonnene Teilnahme noch abgeschlossen werden. Leer: ${String(data.grace_period_default_minutes)} Minuten.`}
        />
        <FormField
          label="Antwortfrist Gewinner (Tage)"
          name="winner_response_days"
          type="number"
          defaultValue={data.winner_response_days}
          min={1}
          max={365}
          placeholder={`Standard: ${String(data.winner_response_default_days)}`}
          help={`So lange hat ein Gewinner Zeit, auf die Gewinnmail zu antworten. Danach verfällt der Gewinn, und es wird nachgezogen. Leer: ${String(data.winner_response_default_days)} Tage.`}
        />
        <FormField
          label="Nachziehungen je Gewinn"
          name="substitute_draw_limit"
          type="number"
          defaultValue={data.substitute_draw_limit}
          required
          min={0}
          max={5}
          help="Wie oft für einen verfallenen Gewinn ein neuer Gewinner gezogen wird. Ist die Zahl erreicht, erhalten Sie eine Nachricht und können unter »Gewinne« von Hand neu ziehen."
        />
      </div>
      <datalist id="zeitzonen">
        {TIME_ZONES.map((zone) => (
          <option key={zone} value={zone} />
        ))}
      </datalist>
    </SaveForm>
  );
}

/**
 * Archive and the way back, the superadmin's (02.11.2026). A running action ends first, so
 * the section does not appear for it.
 */
function ArchiveSection({
  data,
  onDone,
}: {
  readonly data: ActionDetail;
  readonly onDone: () => void;
}): ReactElement | null {
  if (data.status === "archived") {
    return (
      <Section title="Archiv">
        <ConfirmButton
          label="Reaktivieren"
          question={`»${data.title}« reaktivieren?`}
          consequence="Die Aktion kehrt in ihren Status vor dem Archivieren zurück, als Entwurf oder als beendete Aktion."
          confirmLabel="Reaktivieren"
          run={() => moveAction("reactivate", data.action_id)}
          onDone={onDone}
        />
      </Section>
    );
  }
  if (data.status !== "draft" && data.status !== "finished") {
    return null;
  }
  return (
    <Section title="Archiv" tone="danger">
      <ConfirmButton
        label="Archivieren"
        question={`»${data.title}« archivieren?`}
        consequence="Die Aktion verschwindet aus allen Listen und steht danach unter Aktionen › Archiv. Dort lässt sie sich reaktivieren."
        confirmLabel="Archivieren"
        run={() => moveAction("archive", data.action_id)}
        onDone={onDone}
      />
    </Section>
  );
}

type Channel = ActionDetail["notification_channels"][number];

/** The two tables, the second named after the firm of the calendar (E8). */
function audiences(wording: Wording): readonly (readonly [Channel["audience"], string])[] {
  return [
    ["publisher", "Für das Backoffice"],
    ["sponsor", wording.audience],
  ];
}

/**
 * Bell and mail per event (D56), one row per event of the server's catalog, the publisher's
 * events first. Every row is sent, so the server stores what differs from its default.
 */
function NotificationSettingsForm({
  data,
  readOnly,
  onSaved,
}: {
  readonly data: ActionDetail;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
}): ReactElement {
  const channels = data.notification_channels;
  return (
    <SaveForm
      label="Benachrichtigungen"
      readOnly={readOnly}
      onSaved={onSaved}
      submit={(form) =>
        saveActionSection(
          "notifications",
          {
            channels: channels.map((channel) => ({
              kind: channel.kind,
              bell: form.has(`bell:${channel.kind}`),
              mail: form.has(`mail:${channel.kind}`),
            })),
          },
          data.action_id,
        )
      }
    >
      {audiences(wordingOf(data)).map(([audience, caption]) => (
        <table className="mb-4 w-full max-w-xl text-sm" key={audience}>
          <caption className="mb-1 text-left font-medium">{caption}</caption>
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 font-normal">Ereignis</th>
              <th className="w-20 py-1 text-center font-normal">Glocke</th>
              <th className="w-20 py-1 text-center font-normal">E-Mail</th>
            </tr>
          </thead>
          <tbody>
            {channels
              .filter((channel) => channel.audience === audience)
              .map((channel) => (
                <tr className="border-t" key={channel.kind}>
                  <th className="py-1.5 text-left font-normal" scope="row">
                    {channel.label}
                  </th>
                  {(["bell", "mail"] as const).map((kind) => (
                    <td className="text-center" key={kind}>
                      <input
                        aria-label={`${channel.label}: ${kind === "bell" ? "Glocke" : "E-Mail"}`}
                        className="border border-input"
                        name={`${kind}:${channel.kind}`}
                        type="checkbox"
                        defaultChecked={channel[kind]}
                      />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      ))}
    </SaveForm>
  );
}

/**
 * Tab „Einstellungen" of an action (`/aktionen/:actionId/einstellungen`), with the design or
 * the settings permission: the facts, who maintains the doors (written with the sponsors
 * permission as well, so a sponsors-only bundle sets it per sponsor only), the organizer, the
 * texts, the website, and folded away what is technical. One write per form, each with its own
 * audit row on the server. Going live, the test mark, ending and archiving stand in the
 * action's header (`ActionLifecycle`).
 *
 * The schedule is shown, and edited by the superadmin alone, who also archives and brings back
 * (02.11.2026): a moved day moves every door, a new link breaks print. The registration form is a
 * view of the design tab (`FormEditor`); here stands only the proposal of a new action that nobody saved
 * yet, at the top, so it is saved before anybody goes live. Terms are published, always as a
 * new version, with the settings permission; the identity service changes only in draft.
 */
export function ActionSettingsPage(): ReactElement {
  const detail = useActionDetail();
  if (detail.status !== "ready") {
    return <DetailState status={detail.status} />;
  }
  return <SettingsBody key={detail.data.action_id} data={detail.data} reload={detail.reload} />;
}

function SettingsBody({
  data,
  reload,
}: {
  readonly data: ActionDetail;
  readonly reload: () => void;
}): ReactElement {
  const { actions, granted, superadmin, reloadOverview, participantBaseUrl } = useActionContext();
  const ended = data.status === "finished" || data.status === "archived";
  // A customer's calendar has no approval way (E5): no deadlines, no sponsor workflow, and
  // so nothing of either to hand on.
  const customer = (data.customer_sponsor_id ?? null) !== null;
  // Each form is written by its own element (D57); one not granted does not appear, a few
  // show what they hold as a line instead.
  const identityEditable = can(granted, "action_identity") && data.status === "draft";
  // Each group needs the elements of the forms it copies, as the server checks (D55, D57).
  const transferable = customer ? [] : copyableGroups(SETTING_GROUPS, granted);
  // The header's title and obstacles come from the overview, so both are reloaded.
  const saved = (): void => {
    reload();
    reloadOverview();
  };
  const termsLine =
    data.terms === null
      ? "Noch keine Fassung veröffentlicht."
      : `Fassung ${String(data.terms.version)}${
          data.terms_published_at === null ? "" : ` vom ${formatDateTime(data.terms_published_at)}`
        }.`;

  return (
    <>
      <FormProposal detail={data} onSaved={saved} />

      {!ended && data.warnings.length > 0 ? (
        <Notice tone="info" className="mb-8">
          <ul className="list-disc pl-5">
            {data.warnings.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </Notice>
      ) : null}

      <Section title="Grunddaten">
        {/* The superadmin edits the schedule below; everybody else reads it here. */}
        <dl className="mb-6 grid max-w-xl grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Marke</dt>
          <dd>{data.brand_name}</dd>
        </dl>
        <dl
          className="mb-6 grid max-w-xl grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm"
          hidden={superadmin}
        >
          <dt className="text-muted-foreground">Link</dt>
          <dd className="font-mono">{data.stable_link_slug}</dd>
          <dt className="text-muted-foreground">Zeitraum</dt>
          <dd>
            {formatDate(data.starts_on)} bis {formatDate(data.ends_on)}
          </dd>
          <dt className="text-muted-foreground">Türchen</dt>
          <dd>{data.field_count}</dd>
          <dt className="text-muted-foreground">Zeitzone</dt>
          <dd>{data.timezone}</dd>
          <dt className="text-muted-foreground">Nachlauffrist</dt>
          <dd>
            {data.followup_period_days === null
              ? "keine"
              : `${String(data.followup_period_days)} Tage`}
          </dd>
        </dl>
        {superadmin ? <ScheduleForm data={data} readOnly={ended} onSaved={saved} /> : null}
        <SaveForm
          label={customer ? "Titel" : "Titel und Fristen"}
          hidden={!can(granted, "action_basics")}
          readOnly={ended || !can(granted, "action_basics")}
          onSaved={saved}
          submit={(form) =>
            saveActionSection(
              "basics",
              {
                title: text(form, "title"),
                sender_name: text(form, "sender_name"),
                submission_lead_days: numberOrNull(form, "submission_lead_days"),
                approval_lead_days: numberOrNull(form, "approval_lead_days"),
                minimum_booking_deadline: textOrNull(form, "minimum_booking_deadline"),
                deadline_alert_emails: addressesOf(text(form, "deadline_alert_emails")),
              },
              data.action_id,
            )
          }
        >
          <FormField label="Titel" name="title" defaultValue={data.title} required maxLength={200} />
          <FormField
            label="Absendername im Postfach"
            name="sender_name"
            defaultValue={data.sender_name}
            maxLength={200}
            placeholder={data.brand_name}
          />
          {/* Hidden, not unmounted: the save sends what they hold, so a customer's calendar
              keeps its values unchanged (E5). */}
          <div hidden={customer}>
            <FormField
              label="Einreichfrist (Tage vor Öffnung)"
              name="submission_lead_days"
              type="number"
              min={0}
              max={365}
              defaultValue={data.submission_lead_days}
              help={HELP_TIPS.submissionLead}
            />
            <FormField
              label="Freigabefrist (Tage vor Öffnung)"
              name="approval_lead_days"
              type="number"
              min={0}
              max={365}
              defaultValue={data.approval_lead_days}
              help={HELP_TIPS.approvalLead}
            />
            <div className="mt-4 grid gap-2.5">
              <div className="flex items-center gap-2.5">
                <Label htmlFor="deadline_alert_emails">Fristwarnungen an</Label>
                <HelpTip topic="Fristwarnungen an">{HELP_TIPS.deadlineAlerts}</HelpTip>
              </div>
              <Textarea
                className="bg-background"
                id="deadline_alert_emails"
                name="deadline_alert_emails"
                rows={2}
                defaultValue={data.deadline_alert_emails.join("\n")}
                aria-describedby="deadline_alert_emails_hint"
              />
              <p className="text-sm text-muted-foreground" id="deadline_alert_emails_hint">
                Bis zu fünf E-Mails, je Zeile eine. Leer: Antwort-E-Mail der Marke.
              </p>
            </div>
            <FormField
              label="Mindestbelegung bis"
              name="minimum_booking_deadline"
              type="date"
              defaultValue={data.minimum_booking_deadline}
              help={HELP_TIPS.minimumBooking}
            />
          </div>
        </SaveForm>
      </Section>

      {/* A customer's calendar has no sponsors to maintain doors (E5); its customer's access
          takes the place, under the same element (E6b). */}
      {can(granted, "sponsor_workflow") && !customer ? (
        <Section title="Pflege durch Sponsoren">
          <SponsorWorkflowForm data={data} readOnly={ended} onSaved={saved} />
        </Section>
      ) : null}
      {can(granted, "sponsor_workflow") && customer ? (
        <Section title="Kalenderzugang">
          <CalendarAccessForm data={data} readOnly={ended} onSaved={saved} />
        </Section>
      ) : null}

      {can(granted, "action_notifications") ? (
        <Section
          title={
            <span className="inline-flex items-center gap-1">
              Benachrichtigungen
              <HelpTip topic="Benachrichtigungen">
                {(customer ? CUSTOMER_HELP_TIPS : HELP_TIPS).notifications}
              </HelpTip>
            </span>
          }
        >
          <NotificationSettingsForm data={data} readOnly={ended} onSaved={saved} />
        </Section>
      ) : null}

      {transferable.length > 0 ? (
        <Section>
          <StyleTransfer source={data} actions={actions} groups={transferable} />
        </Section>
      ) : null}

      {can(granted, "action_organizer") ? (
        <Section title="Veranstalter">
          <SaveForm
            label="Veranstalter"
            readOnly={ended}
            onSaved={saved}
            submit={(form) =>
              saveActionSection(
                "organizer",
                {
                  name: text(form, "organizer_name"),
                  address: textOrNull(form, "organizer_address"),
                  imprint_url: text(form, "organizer_imprint_url"),
                  privacy_url: text(form, "privacy_policy_url"),
                  minimum_age: numberOrNull(form, "minimum_age"),
                },
                data.action_id,
              )
            }
          >
            <FormField
              label="Name"
              name="organizer_name"
              defaultValue={data.organizer_name}
              required
              maxLength={200}
            />
            <FormField
              label="Anschrift"
              name="organizer_address"
              defaultValue={data.organizer_address}
              maxLength={2000}
            />
            <FormField
              label="Impressum-Link"
              name="organizer_imprint_url"
              type="url"
              defaultValue={data.organizer_imprint_url}
              required
              maxLength={400}
            />
            <FormField
              label="Datenschutz-Link"
              name="privacy_policy_url"
              type="url"
              defaultValue={data.privacy_policy_url}
              required
              maxLength={400}
            />
            <FormField
              label="Mindestalter"
              name="minimum_age"
              type="number"
              min={0}
              max={99}
              defaultValue={data.minimum_age}
            />
          </SaveForm>
        </Section>
      ) : null}

      {/* The fallback content went to Gestaltung › Türchen-Fenster on 23.10.2026 (D88). */}
      <Section title="Texte">
        {can(granted, "action_terms") ? (
          <SaveForm
            label="Teilnahmebedingungen"
            readOnly={ended}
            onSaved={saved}
            submitLabel="Neue Fassung veröffentlichen"
            submit={(form) =>
              saveActionSection("terms", { body: text(form, "terms_body") }, data.action_id)
            }
          >
            <Label htmlFor="terms_body">Teilnahmebedingungen</Label>
            <p className="mt-1 text-sm text-muted-foreground" id="terms_hint">
              {termsLine} Jede Veröffentlichung ist eine neue Fassung, frühere bleiben erhalten.
            </p>
            <Textarea
              className="mt-1.5 bg-background"
              id="terms_body"
              name="terms_body"
              rows={10}
              required
              maxLength={50000}
              defaultValue={data.terms?.body ?? ""}
              aria-describedby="terms_hint"
            />
          </SaveForm>
        ) : (
          <p className="mt-6 text-sm">
            <span className="font-medium">Teilnahmebedingungen:</span> {termsLine}
          </p>
        )}
      </Section>

      {can(granted, "action_embed") ? (
        <Section title="Website">
          <EmbedSettings
            data={data}
            participantBaseUrl={participantBaseUrl}
            readOnly={ended}
            onSaved={saved}
          />
        </Section>
      ) : null}

      {superadmin ? <ArchiveSection data={data} onDone={saved} /> : null}

      {/* Its own heading, or the identity service would belong to "Website" in the outline. The
          customer account it serves is offered in the design tab's view "Formular" (D52). */}
      <h2 className="sr-only">Erweitert</h2>
      <Advanced summary="Erweitert" className="mt-2">
        {can(granted, "action_identity") ? (
          <SaveForm
            label="Anmeldedienst"
            readOnly={ended || !identityEditable}
            onSaved={saved}
            submit={(form) =>
              saveActionSection(
                "identity",
                {
                  issuer: textOrNull(form, "oidc_issuer"),
                  client_id: textOrNull(form, "oidc_client_id"),
                },
                data.action_id,
              )
            }
          >
            <FormField
              label="Aussteller"
              name="oidc_issuer"
              type="url"
              defaultValue={data.oidc_issuer}
              maxLength={400}
            />
            <FormField
              label="Client-Kennung"
              name="oidc_client_id"
              defaultValue={data.oidc_client_id}
              maxLength={200}
            />
            {identityEditable ? null : (
              <p className="mt-3 text-sm text-muted-foreground">Nur im Entwurf änderbar.</p>
            )}
          </SaveForm>
        ) : (
          // Without the element the identity service is not offered, but whether
          // one is entered decides whether the form offers a customer account at all.
          <p className="mt-6 text-sm">
            <span className="font-medium">Anmeldedienst:</span>{" "}
            {data.oidc_issuer === null
              ? "keiner eingetragen, ein Kundenkonto wird nicht angeboten."
              : "eingetragen."}
          </p>
        )}
      </Advanced>
    </>
  );
}
